import { TILE_SIZE } from '@/microfrontends/map/shared/constants/map';
import { CanvasDrawingService } from '@/microfrontends/map/shared/services/CanvasDrawingService';
import { PinService } from '@/microfrontends/map/shared/services/PinService';
import { type TPromoPinConfig } from '@/microfrontends/map/shared/types/PromoPinConfig';
import { getTilePixelCoords } from '@/microfrontends/map/shared/utils/map/tiles';

import { type Cluster } from '@/packages/query-state/shared/types/clusters/ClustersResponse';

import { PulseAnimator } from '../../../PulseAnimator';

interface IDrawTileOptions {
  tileClusters: Cluster[];
  tileNumber: YMaps.TTileNumber;
  tileZoom: number;
  pulseProgress?: number;
}

interface IDrawClusterOptions {
  cluster: Cluster;
  pinCoords: [number, number];
  pulseProgress?: number;
}

export type CreateTileClassOptions = {
  getClustersForTile(tileNumber: YMaps.TTileNumber, zoom: number): Cluster[];
  mapProjection: YMaps.projection.wgs84Mercator;
  dpr: number;
  getPromoPinConfig: () => TPromoPinConfig;
};

export function createTileConstructor(options: CreateTileClassOptions): YMaps.IDomTileConstructor {
  const { getClustersForTile, mapProjection, dpr, getPromoPinConfig } = options;

  return class TileClass implements YMaps.IDomTile {
    private tileNumber: YMaps.TTileNumber;
    private tileZoom: number;
    private canvasElement: HTMLCanvasElement | undefined;
    private canvasContext: CanvasRenderingContext2D | undefined;
    private attached: boolean;
    private hash: string;
    private canvasDrawingService: CanvasDrawingService | undefined;

    // Интерфейс требует два обязательных первых параметра, но мы их не используем
    public constructor(
      _url: string | null,
      _container: YMaps.layer.tileContainer.DomContainer,
      initOptions: YMaps.IDomTileOptions,
    ) {
      this.tileNumber = initOptions.tileNumber;
      this.tileZoom = initOptions.tileZoom;
      this.attached = false;
      this.hash = '';
      this.canvasDrawingService = undefined;
    }

    public destroy(): void {
      if (this.canvasElement) {
        this.canvasElement.width = 0;
        this.canvasElement.height = 0;

        if (this.canvasElement.parentElement) {
          this.canvasElement.parentElement.removeChild(this.canvasElement);
        }

        this.canvasElement = undefined;
        this.canvasContext = undefined;
        this.canvasDrawingService = undefined;
      }
      this.attached = false;
    }

    public isReady(): boolean {
      return true;
    }

    public renderAt = (context: HTMLElement, bounds: [[number, number], [number, number]]): void => {
      if (!this.canvasElement) {
        this.initCanvas();
      }

      const tileClusters = getClustersForTile(this.tileNumber, this.tileZoom);

      tileClusters.sort((a, b) => {
        if (a.properties?.pulsedGlow && !b.properties?.pulsedGlow) {
          return 1;
        }

        if (!a.properties?.pulsedGlow && b.properties?.pulsedGlow) {
          return -1;
        }

        return a.id.split('').reverse().join('') < b.id.split('').reverse().join('') ? -1 : 1;
      });

      const clustersHash = tileClusters
        .map(cluster => cluster.geometry.coordinates.join(','))
        .sort()
        .join(' ');

      const tileHash = `${this.tileNumber};${this.tileZoom};${clustersHash}`;

      if (this.hash !== tileHash) {
        /**
         * @description Рисуем обычные пины
         */
        this.drawTile({ tileClusters, tileNumber: this.tileNumber, tileZoom: this.tileZoom });

        /**
         * @description Рисуем пульсирующие пины
         */
        this.drawTileWithPulse({ tileClusters, tileNumber: this.tileNumber, tileZoom: this.tileZoom });

        this.hash = tileHash;
      }

      const canvas = this.canvasElement as HTMLCanvasElement;
      const [[topLeftX, topLeftY], [bottomRightX, bottomRightY]] = bounds;

      canvas.style.top = `${topLeftY}px`;
      canvas.style.left = `${topLeftX}px`;
      canvas.style.width = `${bottomRightX - topLeftX}px`;
      canvas.style.height = `${bottomRightY - topLeftY}px`;

      if (!this.attached) {
        context.appendChild(canvas);
        this.attached = true;
      }
    };

    private initCanvas = (): void => {
      this.canvasElement = document.createElement('canvas');
      this.canvasElement.width = TILE_SIZE * dpr;
      this.canvasElement.height = TILE_SIZE * dpr;
      this.canvasElement.style.position = 'absolute';
      this.canvasElement.style.textRendering = 'geometricPrecision';
      this.canvasContext = this.canvasElement.getContext('2d') as CanvasRenderingContext2D;

      this.canvasDrawingService = new CanvasDrawingService({ context: this.canvasContext, dpr });
    };

    private drawTileWithPulse = (tileOptions: IDrawTileOptions): void => {
      const promoPinConfig = getPromoPinConfig();

      const pulseAnimator = new PulseAnimator(
        pulseProgress => this.drawTile({ ...tileOptions, pulseProgress }),
        promoPinConfig.duration,
      );

      if (this.tileZoom >= promoPinConfig.activeZoomPin) {
        pulseAnimator.startAnimation();
      } else {
        pulseAnimator.stopAnimation();
      }
    };

    private drawTile = ({ tileClusters, tileNumber, tileZoom, pulseProgress }: IDrawTileOptions): void => {
      if (!this.canvasElement || !this.canvasDrawingService) {
        return;
      }

      const tilePixelCoords = getTilePixelCoords(tileNumber);

      this.canvasDrawingService.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);

      tileClusters.forEach(tileCluster => {
        const pinCoords = this.getClusterTileCoords(tileCluster, tilePixelCoords, tileZoom);

        this.drawPin({ cluster: tileCluster, pinCoords, pulseProgress });
      });
    };

    private getClusterTileCoords(
      cluster: Cluster,
      tilePixelCoords: YMaps.TPixelCoord,
      tileZoom: number,
    ): [number, number] {
      const [tilePixelCoordX, tilePixelCoordY] = tilePixelCoords;
      const [clusterCoordX, clusterCoordY] = cluster.geometry.coordinates;

      const [clusterPixelCoordX, clusterPixelCoordY] = mapProjection.toGlobalPixels(
        [clusterCoordX, clusterCoordY],
        tileZoom,
      );

      return [clusterPixelCoordX - tilePixelCoordX, clusterPixelCoordY - tilePixelCoordY];
    }

    private drawPin = ({ cluster, pinCoords, pulseProgress }: IDrawClusterOptions): void => {
      if (!this.canvasDrawingService) {
        return;
      }

      const pinService = new PinService({ cluster });
      const { fillColor, borderColor, textColor, glowPulsedColor } = pinService.provideColors();
      const { borderWidth, radius, glowRadius } = pinService.provideSizes();
      const withPulseGlow = pinService.provideIsWithPulsedGlow();
      const withGlow = pinService.provideIsWithGlow();
      const withCounter = pinService.provideIsPinWithCounter();

      /**
       * @description
       * Рисуем свечение вокруг пина с VAS
       */
      if (withPulseGlow && pulseProgress) {
        this.canvasDrawingService.drawGlowPulse({
          color: glowPulsedColor,
          pinCoords,
          radius,
          pulseProgress,
          pulseRadius: getPromoPinConfig().pulseRadius,
        });
      } else if (withGlow && glowRadius) {
        this.canvasDrawingService.drawGlow({ color: fillColor, pinCoords, glowRadius });
      }

      /**
       * @description
       * Реализуем рамку вокруг пина с помощью двух окружностей друг в друге.
       * Рисуем внешнюю окружность пина.
       */
      if (borderWidth > 0) {
        this.canvasDrawingService.drawCircle({ color: borderColor, pinCoords, radius });
      }

      /**
       * @description
       * Рисуем внутреннюю окружность пина
       */
      this.canvasDrawingService.drawCircle({ color: fillColor, pinCoords, radius: radius - borderWidth });

      /**
       * @description
       * Рисуем текст в пине
       */
      if (withCounter) {
        const count = pinService.provideCount();

        this.canvasDrawingService.drawTextInCoordinates({ color: textColor, pinCoords, text: count });
      }
    };
  };
}
